// Form state to request body, and back.
//
// Kept out of the component so the rules that actually matter are stated once and testable:
// amounts stay strings, blanks become null, and an incomplete quantity or price is not sent at all.
//
// The form carries what identifies a deal (TX-US-0031, TX-US-0039): its number, direction, state,
// one counterparty, product, trader, volume, price and notes. A deal number left blank is sent as
// null, and the server gives the deal its own identifier as its number. The internal id is
// Auremin's own reference for the deal: nothing fills it in, and left blank it stays blank.
//
// Auremin is on one side of every deal. The direction says which, and names the other side: the
// supplier when Auremin buys, the customer when it sells. The price is per unit of the volume, and
// the value is volume x price - shown as it is typed, computed again by the server, never sent.
//
// The deal flow (TX-US-0043) adds the terms a proposal is approved on: the Incoterm and its named
// place, the quantity tolerance, the payment terms, the delivery period, origin and destination,
// and the logistics. With the flow on, the state is not the form's: the stage is moved on the
// deal's page and the state follows it, so the request carries none.

import { namedPlaceLabel } from './vocabulary.js';

export const MAX_DEAL_NUMBER_LENGTH = 40;
export const MAX_INTERNAL_ID_LENGTH = 40;
export const MAX_TRADER_LENGTH = 120;

export const DEFAULT_CURRENCY = 'USD';

export const DIRECTIONS = [
  { value: 'BUY', label: 'Buy' },
  { value: 'SELL', label: 'Sell' },
];

export const STATES = [
  { value: 'POTENTIAL', label: 'Potential' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'CLOSED', label: 'Closed' },
];

export function counterpartyLabel(direction) {
  if (direction === 'BUY') return 'Supplier';
  if (direction === 'SELL') return 'Customer';
  return 'Counterparty';
}

export function stateLabel(state) {
  return STATES.find((entry) => entry.value === state)?.label ?? state;
}

export function directionLabel(direction) {
  return DIRECTIONS.find((entry) => entry.value === direction)?.label ?? '';
}

export function emptyDraft() {
  return {
    dealNumber: '',
    internalId: '',
    trader: '',
    // No direction until someone picks one: guessing which side Auremin is on would be worse
    // than asking.
    direction: '',
    state: 'POTENTIAL',
    counterpartyName: '',
    counterpartyId: null,
    product: '',
    quantityAmount: '',
    quantityUnit: '',
    priceAmount: '',
    priceCurrency: DEFAULT_CURRENCY,
    incoterm: '',
    namedPlace: '',
    tolerance: '',
    paymentTerms: '',
    periodFrom: '',
    periodTo: '',
    origin: '',
    destination: '',
    logistics: '',
    notes: '',
  };
}

export function fromDeal(deal) {
  const fields = deal.fields ?? {};
  return {
    ...emptyDraft(),
    dealNumber: fields.deal_number ?? '',
    internalId: fields.internal_id ?? '',
    trader: fields.trader ?? '',
    direction: fields.direction ?? '',
    state: deal.state ?? 'POTENTIAL',
    counterpartyName: fields.counterparty?.display_name ?? '',
    counterpartyId: fields.counterparty?.counterparty_id ?? null,
    product: fields.product ?? '',
    quantityAmount: fields.quantity?.amount ?? '',
    quantityUnit: fields.quantity?.unit ?? '',
    priceAmount: fields.price?.amount ?? '',
    priceCurrency: fields.price?.currency ?? DEFAULT_CURRENCY,
    incoterm: fields.incoterms ?? '',
    namedPlace: fields.named_place ?? '',
    tolerance: fields.quantity_tolerance_pct ?? '',
    paymentTerms: fields.payment_terms ?? '',
    periodFrom: fields.delivery_period?.start ?? '',
    periodTo: fields.delivery_period?.end ?? '',
    origin: fields.origin ?? '',
    destination: fields.destination ?? '',
    logistics: fields.logistics ?? '',
    notes: fields.notes ?? '',
  };
}

const blankToNull = (value) => (value && value.trim() ? value.trim() : null);

export function toRequestBody(draft, { flow = false } = {}) {
  // Both halves or neither. An amount without a unit is not a quantity, and the server would
  // refuse it against the unit list with an error naming a field the user did fill in.
  const quantity =
    draft.quantityAmount.trim() && draft.quantityUnit
      ? { amount: draft.quantityAmount.trim(), unit: draft.quantityUnit }
      : null;
  const price = draft.priceAmount.trim()
    ? { amount: draft.priceAmount.trim(), currency: draft.priceCurrency || DEFAULT_CURRENCY }
    : null;

  // Either end may be open while the terms are negotiated; submitting the proposal needs both.
  const deliveryPeriod = draft.periodFrom || draft.periodTo
    ? { start: draft.periodFrom || null, end: draft.periodTo || null }
    : null;

  const body = {
    deal_number: blankToNull(draft.dealNumber),
    internal_id: blankToNull(draft.internalId),
    trader: blankToNull(draft.trader),
    direction: draft.direction || null,
    state: draft.state,
    counterparty: reference(draft.counterpartyName, draft.counterpartyId),
    product: blankToNull(draft.product),
    quantity,
    price,
    incoterms: draft.incoterm || null,
    named_place: blankToNull(draft.namedPlace),
    quantity_tolerance_pct: blankToNull(draft.tolerance),
    payment_terms: draft.paymentTerms || null,
    delivery_period: deliveryPeriod,
    origin: draft.origin || null,
    destination: draft.destination || null,
    logistics: blankToNull(draft.logistics),
    notes: blankToNull(draft.notes),
  };
  // The API refuses a state with the flow on (AC19): absent, not null.
  if (flow) delete body.state;
  return body;
}

// The fields a save requires, keyed as the server keys them; the form marks each. The direction
// always. With the flow on, a deal is not created, nor edited at Proposal, without what it is
// about (AC1). Later stages do not ask: a deal entered before the flow may lack an Incoterm.
export function requiredFields({ flow = false, stage = 'PROPOSAL' } = {}) {
  return flow && stage === 'PROPOSAL'
    ? ['direction', 'counterparty', 'product', 'incoterms']
    : ['direction'];
}

const MISSING = {
  direction: (draft) => !draft.direction && 'Choose whether Auremin buys or sells.',
  counterparty: (draft) => !blankToNull(draft.counterpartyName) && 'Choose the counterparty.',
  product: (draft) => !blankToNull(draft.product) && 'Choose the product.',
  incoterms: (draft) => !draft.incoterm && 'Choose the Incoterm.',
};

// What the form refuses before asking the server, keyed as the server keys its own refusals.
export function validate(draft, context = {}) {
  const errors = {};
  for (const field of requiredFields(context)) {
    const refusal = MISSING[field](draft);
    if (refusal) errors[field] = refusal;
  }
  return errors;
}

// Free text is carried through with no id, and the server flags it unresolved. The form never
// invents a counterparty record.
function reference(name, id) {
  const display = blankToNull(name);
  return display ? { counterparty_id: id ?? null, display_name: display } : null;
}

// The deal value as the form shows it while typing: volume x price, in the price's currency, or
// null while either is missing or not a number. Exact decimal arithmetic on the digits typed - a
// float would show a value the server does not store.
export function dealValue(draft) {
  const product = multiplyDecimals(draft.quantityAmount, draft.priceAmount);
  if (product === null) return null;
  return { amount: product, currency: draft.priceCurrency || DEFAULT_CURRENCY };
}

const DECIMAL = /^\d+(\.\d+)?$/;

export function multiplyDecimals(left, right) {
  const a = (left ?? '').trim();
  const b = (right ?? '').trim();
  if (!DECIMAL.test(a) || !DECIMAL.test(b)) return null;

  const scale = fractionDigits(a) + fractionDigits(b);
  const digits = (BigInt(a.replace('.', '')) * BigInt(b.replace('.', ''))).toString();
  if (scale === 0) return digits;

  const padded = digits.padStart(scale + 1, '0');
  const whole = padded.slice(0, -scale);
  const fraction = padded.slice(-scale).replace(/0+$/, '');
  return fraction ? `${whole}.${fraction}` : whole;
}

function fractionDigits(value) {
  const point = value.indexOf('.');
  return point === -1 ? 0 : value.length - point - 1;
}

// "1234567.5" as "1,234,567.5": grouped on the string, so no digit passes through a float.
export function groupDigits(amount) {
  const [whole, fraction] = String(amount).split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return fraction ? `${grouped}.${fraction}` : grouped;
}

export function formatMoney(money) {
  return money ? `${money.currency} ${groupDigits(money.amount)}` : '';
}

export function formatPrice(price, unit) {
  if (!price) return '';
  return unit ? `${formatMoney(price)} / ${unit}` : formatMoney(price);
}

// The form's label for each term an approved round locks (trex/deals/terms.py LOCKED_TERMS), for
// the refusal of an edit that changes one. The counterparty is named by what it is on the deal,
// and the named place by the deal's Incoterm.
const TERM_LABELS = {
  direction: 'Auremin',
  product: 'Product',
  quantity: 'Volume',
  quantity_tolerance_pct: 'Quantity tolerance ±%',
  price: 'Price',
  incoterms: 'Incoterm',
  payment_terms: 'Payment terms',
  delivery_period: 'Delivery period',
};

export function termLabel(name, { direction, namedPlace }) {
  if (name === 'counterparty') return counterpartyLabel(direction);
  if (name === 'named_place') return namedPlace;
  return TERM_LABELS[name] ?? name;
}

// What a refused save says (TX-US-0043): the approved terms it would change, by their labels; a
// refusal at the deal's stage, in the API's words; or, for a version someone else has saved since,
// the way back.
export function editRefusal(err, labels) {
  const locked = err.detail?.locked ?? [];
  if (locked.length > 0) {
    const names = locked.map((name) => termLabel(name, labels)).join(', ');
    return `Approved terms are locked: ${names}. Reopen the proposal to change them.`;
  }
  if (err.detail?.kind === 'deal_locked') return `This edit was refused: ${err.message}.`;
  return 'Someone else changed this deal while you were editing it. '
    + 'Reload to see their version before saving again.';
}

// What a refused save of `draft` shows, on the form and on the deal's page alike: a 409 in words
// above the form (editRefusal), with the terms named as the draft labels them, and whether it is
// a version someone else saved since, which a reload answers; the API's field errors beside their
// fields; anything else as it came.
export function refusedSave(err, draft, vocabulary) {
  if (err.status === 409) {
    const labels = {
      direction: draft.direction,
      namedPlace: namedPlaceLabel(vocabulary, draft.incoterm),
    };
    const stale = err.detail?.kind !== 'deal_locked';
    return { error: editRefusal(err, labels), stale, fieldErrors: {} };
  }
  if (err.fields) return { error: null, stale: false, fieldErrors: err.fields };
  return { error: err.message, stale: false, fieldErrors: {} };
}
